import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { ToastAction, ToastVariant } from '@/components/ui/toast';
import { useCommandContext } from '@/context/CommandContext';
import { restoreNodesQuery } from '@/services/trash.service';
import { type TrashNodesResponse } from '@/types/trash';
import { useToast } from '@/components/ui/use-toast';
import { DAMNodeType, NodeType } from '@/types/tree';
import { errorProd } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

// The node was found but has already left the bin. Distinct from a 404, which means the id
// names nothing at all - see NodesNotInTrashException in dam-backend.
const HTTP_CONFLICT = 409;

/**
 * What `damRequest` throws for a failed restore: the parsed body with the HTTP status
 * added. On a 409 the body names the ids that lost the race in `nodes`.
 */
type RestoreNodesError = {
  message?: string;
  status?: number;
  nodes?: Array<string>;
};

/**
 * The conflicted ids are not the same number as the caller asked for: the restore is a
 * single transaction, so two conflicts out of ten roll all ten back. The message is about
 * the conflict, so it counts the conflict. Falls back to the request when the list is
 * absent, which is all an older backend answers.
 */
const getConflictCount = (error: RestoreNodesError | null, requested = 0) => error?.nodes?.length ?? requested;

export const useRestoreNodesMutation = () => {
  const queryClient = useQueryClient();
  const restoreNodesQueryFn = useAuthenticatedQueryFn(restoreNodesQuery);
  const tToasts = useTranslations('toasts');
  const tTree = useTranslations('tree');
  const tCommon = useTranslations('common');
  const { toast } = useToast();
  const { undo } = useCommandContext();

  const getItemLabel = (type: string | undefined, count: number) => {
    switch (type) {
      case DAMNodeType.Asset:
      case NodeType.Assets:
        return tTree('asset', { count });
      case DAMNodeType.Album:
      case NodeType.Albums:
        return tTree('album', { count });
      case DAMNodeType.Folder:
      case NodeType.Folders:
        return tTree('folder', { count });
      default:
        return tTree('item', { count });
    }
  };

  const getTitleItemLabel = (label: string) => {
    if (!label) {
      return label;
    }

    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  // See useTrashNodesMutation's getUploadStoreIds: `params.nodes` are node-tree ids for a
  // multi-selection, a different id space from the asset ids the upload store keys its files
  // by, so a caller that has the real asset ids (delete-node-alert-component.tsx, via
  // TrashNodesCommand's undo) passes them separately.
  const getUploadStoreIds = (params: { nodes?: Array<string>; assetIds?: Array<string> }): Array<string> =>
    params.assetIds && params.assetIds.length > 0 ? params.assetIds : (params.nodes ?? []);

  // A conflict means the nodes are back in place, restored by someone else - the same
  // shape of change a successful restore makes, so the same queries are stale either way
  // and both paths refresh the same set. `Asset` is on it because the inspector reads that
  // single-node record, and a stale one keeps offering Restore for a row already restored.
  const invalidateAfterRestore = (type: string | undefined) => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset], refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Trash], refetchType: 'all' });
    void invalidateAssetDataForNodeChange(queryClient, { refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Duplicates], refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree], refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch], refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ShareList], refetchType: 'all' });

    // Invalidate home only if a folder or an album is restored
    // because assets are not shown under favorites anyway
    if (type === DAMNodeType.Folder || type === DAMNodeType.Album) {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Home], refetchType: 'all' });
    }
  };

  const { mutateAsync: restoreNodesMutation } = useMutation<
    TrashNodesResponse,
    RestoreNodesError,
    Parameters<typeof restoreNodesQuery>['0'] & { assetIds?: Array<string> },
    { idsThatWereTrashed?: Array<string> }
  >({
    onMutate: (params) => {
      let idsThatWereTrashed: Array<string> = [];
      const uploadStoreIds = getUploadStoreIds(params ?? {});

      // Optimistic, mirroring useTrashNodesMutation: unhide the upload-list row as soon as
      // the restore is confirmed, not after the request round-trips.
      if (uploadStoreIds.length > 0) {
        // Remember which of these were actually hidden beforehand, so a failed restore
        // only re-hides the ones this call itself unhid — not an id that was already
        // showing (a no-op here) for some unrelated reason.
        idsThatWereTrashed = useUploadStore.getState().getTrashedFileIds(uploadStoreIds);
        useUploadStore.getState().setFileTrashed(uploadStoreIds, false);
      }

      return { idsThatWereTrashed };
    },
    // assetIds is client-side only (upload-list matching) — the backend only expects
    // nodes/type/undoable, so it's dropped here rather than riding along on the wire.
    mutationFn: (params) => restoreNodesQueryFn({ nodes: params.nodes, type: params.type, undoable: params.undoable }),
    onSuccess: (response, params) => {
      const count = params?.nodes?.length ?? 0;
      const itemLabel = getItemLabel(params?.type, count);
      const titleItemLabel = getTitleItemLabel(itemLabel);
      toast({
        title: tToasts('restoreNodesMutation.title', { itemLabel: titleItemLabel }),
        description: tToasts('restoreNodesMutation.description', { itemLabel, count }),
        ...(params.undoable && {
          action: (
            <ToastAction onClick={undo} altText={tToasts('undoNodeDelete')} icon={Undo2}>
              {tCommon('undo')}
            </ToastAction>
          ),
        }),
      });

      invalidateAfterRestore(params?.type);
    },
    // Restoring something that is no longer in the trash answers 409, not a generic
    // failure. It means another user restored it first and this screen is behind - the
    // inspector is the visible symptom, because it keeps offering Restore for a row that
    // is already back. So refresh what the inspector and the trash read rather than
    // leaving the viewer to guess, and say which of the two things happened.
    //
    // Declaring `onError` here replaces the client-wide mutation handler in `_app.tsx`
    // rather than running alongside it, so the logging that handler owns is repeated. What
    // is worth suppressing is deliberately not: a 401 is dealt with in the request layer,
    // which swaps the page for the auth wall, and no other mutation second-guesses that.
    onError: (error, params, ctx) => {
      const isConflict = error?.status === HTTP_CONFLICT;

      // A 409 means the nodes really are back, just not by this viewer's hand, so onMutate's
      // optimistic unhide turned out right and must stand. Every other failure rolled the
      // transaction back, so re-hide only the rows this call itself unhid.
      if (!isConflict && ctx?.idsThatWereTrashed && ctx.idsThatWereTrashed.length > 0) {
        useUploadStore.getState().setFileTrashed(ctx.idsThatWereTrashed, true);
      }

      // Only a conflict changed anything server-side. Every other failure rolls the
      // transaction back, so there is nothing new to read and nothing to invalidate.
      if (isConflict) {
        invalidateAfterRestore(params?.type);
      }

      // 409 is expected traffic between two users, so it is not worth a console entry.
      // Everything else is: a 4xx we did not plan for, a 5xx, or a response that failed
      // its schema - and that last one never reaches `damRequest`'s own logging, because
      // it is thrown after the fetch resolved.
      if (!isConflict) {
        errorProd(error);
      }

      if (isConflict) {
        toast({
          // Nothing broke - the nodes are back, just not by this viewer's hand - so this
          // does not get the red treatment a real failure does.
          variant: ToastVariant.DEFAULT,
          title: tToasts('restoreNodesConflict.title'),
          description: tToasts('restoreNodesConflict.description', {
            count: getConflictCount(error, params?.nodes?.length ?? 0),
          }),
        });
        return;
      }

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tToasts('restoreNodesFailed.title'),
        description: tToasts('restoreNodesFailed.description', { count: params?.nodes?.length ?? 0 }),
      });
    },
  });

  return {
    restoreNodesMutation,
  };
};
